import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import i18n from "i18next";
import { Alert, Form, Radio, Select } from "antd";
import UriReplacement from "../service/UriReplacement";
import { SkosRepository } from "../../redux";

const SOURCE_MODE = {
  SELECT: "select",
  MANUAL: "manual",
};

function conceptUri(binding) {
  if (typeof binding === "string") return binding;
  const value = binding?.concept;
  return typeof value === "object" ? value?.value : value || "";
}

function uniqueUris(bindings) {
  return Array.from(new Set((Array.isArray(bindings) ? bindings : []).map(conceptUri).filter(Boolean)));
}

export function ConeptUriReplacement({
  open,
  onClose,
  targetUri,
  dispatch,
  initialSourceUri = "",
  ...replacementProps
}) {
  const [sourceMode, setSourceMode] = useState(SOURCE_MODE.SELECT);
  const [sourceUri, setSourceUri] = useState(initialSourceUri);
  const [inSchemeUris, setInSchemeUris] = useState([]);
  const [notInSchemeUris, setNotInSchemeUris] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const loadSequence = useRef(0);

  useEffect(() => {
    if (!open) return undefined;

    const sequence = ++loadSequence.current;
    setSourceMode(SOURCE_MODE.SELECT);
    setSourceUri(initialSourceUri);
    setInSchemeUris([]);
    setNotInSchemeUris([]);
    setLoadError(null);
    setLoading(true);

    Promise.all([
      dispatch(SkosRepository.loadInSchemeConcepts()),
      dispatch(SkosRepository.loadNotInSchemeConcepts()),
    ])
      .then(([inScheme, notInScheme]) => {
        if (sequence !== loadSequence.current) return;
        setInSchemeUris(uniqueUris(inScheme));
        setNotInSchemeUris(uniqueUris(notInScheme));
      })
      .catch((reason) => {
        if (sequence === loadSequence.current) {
          setLoadError(reason?.message || String(reason));
        }
      })
      .finally(() => {
        if (sequence === loadSequence.current) setLoading(false);
      });

    return () => { loadSequence.current += 1; };
  }, [open, dispatch, initialSourceUri]);

  const selectOptions = useMemo(() => {
    const toOptions = (uris) => uris
      .filter((uri) => uri !== targetUri)
      .map((uri) => ({ label: uri, value: uri }));

    return [
      { label: i18n.t("service.conceptUriReplacement.inScheme"), options: toOptions(inSchemeUris) },
      { label: i18n.t("service.conceptUriReplacement.notInScheme"), options: toOptions(notInSchemeUris) },
    ].filter((group) => group.options.length > 0);
  }, [inSchemeUris, notInSchemeUris, targetUri]);

  const handleModeChange = (event) => {
    setSourceMode(event.target.value);
    setSourceUri("");
    setLoadError(null);
  };

  const sourceUriExtra = (
    <>
      <Form.Item label={i18n.t("service.conceptUriReplacement.sourceMode")}>
        <Radio.Group value={sourceMode} onChange={handleModeChange} buttonStyle="solid">
          <Radio.Button value={SOURCE_MODE.SELECT}>{i18n.t("service.conceptUriReplacement.selectConcept")}</Radio.Button>
          <Radio.Button value={SOURCE_MODE.MANUAL}>{i18n.t("service.conceptUriReplacement.enterManually")}</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {sourceMode === SOURCE_MODE.SELECT && (
        <Form.Item label={i18n.t("service.conceptUriReplacement.concept")}>
          <Select
            value={sourceUri || undefined}
            onChange={setSourceUri}
            options={selectOptions}
            loading={loading}
            disabled={loading}
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder={i18n.t("service.conceptUriReplacement.selectSourceUri")}
            popupMatchSelectWidth={false}
          />
        </Form.Item>
      )}

      {loadError && sourceMode === SOURCE_MODE.SELECT && (
        <Alert style={{ marginBottom: 16 }} type="error" showIcon
          message={i18n.t("service.conceptUriReplacement.loadError")} description={loadError} />
      )}
    </>
  );

  return (
    <UriReplacement
      {...replacementProps}
      open={open}
      onClose={onClose}
      sourceUri={sourceUri}
      onSourceUriChange={setSourceUri}
      sourceUriReadOnly={sourceMode === SOURCE_MODE.SELECT}
      sourceUriExtra={sourceUriExtra}
      targetUri={targetUri}
      targetUriReadOnly
      title={replacementProps.title || i18n.t("service.conceptUriReplacement.title")}
    />
  );
}

export default connect()(ConeptUriReplacement);
